import Dexie, { type Table } from 'dexie';
import type { RecipeIngredient, RecipeStep, RecipeSourceType, CookedLogEntry } from '@familyhub/shared';
import { registerCleanup } from '../platform/cleanup';
import type { OutboxEntry, MetaRow, RejectionRow } from '../platform/sync-runner';

/**
 * The Recepten module's local database — a **separate** IndexedDB database
 * (`familyhub-recepten`) from groceries (`familyhub`), agenda (`familyhub-agenda`)
 * and todos (`familyhub-todos`), which is the module-independence boundary on the
 * client. Its table, outbox and cursor never touch another module's store.
 *
 * Rows are stored exactly as the server sends them, tombstones (`deleted_at`)
 * included and filtered at read time — a tombstone is what propagates a delete to
 * the other phone.
 */

export interface RecipeRecord {
  id: string;
  title: string;
  servings: number;
  total_minutes: number | null;
  ingredients: RecipeIngredient[];
  steps: RecipeStep[];
  tags: string[];
  source_type: RecipeSourceType;
  source_url: string | null;
  source_name: string | null;
  image_path: string | null;
  source_images: string[];
  notes: string | null;
  created_by: string | null;
  /** One entry per time it was cooked; length = how often. Empty/absent = never cooked. */
  cooked_log?: CookedLogEntry[] | null;
  /** Client-stamped date the household added the recipe (distinct from `created_at`). */
  added_at?: string | null;
  /** Floated to the top of the overview by the household (migration 038). */
  pinned?: boolean | null;
  /** The household's cijfer, 1–10, or null when not graded yet (migration 052). */
  grade?: number | null;
  created_at?: string;
  updated_at: string;
  deleted_at: string | null;
}

export class RecipeDb extends Dexie {
  recepten!: Table<RecipeRecord, string>;
  outbox!: Table<OutboxEntry, number>;
  rejections!: Table<RejectionRow, string>;
  meta!: Table<MetaRow, string>;

  constructor() {
    super('familyhub-recepten');
    this.version(1).stores({
      recepten: 'id, title, updated_at, deleted_at',
      outbox: '++seq, queued_at',
      rejections: 'id, at',
      meta: 'key',
    });
    // v2 added the week planner's `recept_plan` store; v3 drops it again — the
    // planner was replaced by a pin on the recipe row (migration 038). Dexie applies
    // the version chain in order, so a client that reached v2 upgrades cleanly by
    // deleting the store, and a fresh install creates then drops it (a no-op).
    this.version(2).stores({
      recept_plan: 'id, planned_on, recept_id, updated_at, deleted_at',
    });
    this.version(3).stores({
      recept_plan: null,
    });
  }
}

export const rdb = new RecipeDb();

/** The Dexie tables the sync runner mirrors, keyed by the server's change bucket. */
export const RECEPT_TABLE_MAP = [
  { name: 'recepten', table: rdb.recepten as unknown as Table<Record<string, unknown>, string> },
];

/** Wipe on logout — registered with the platform cleanup registry. */
export async function clearRecipeData(): Promise<void> {
  await Promise.all([
    rdb.recepten.clear(),
    rdb.outbox.clear(),
    rdb.rejections.clear(),
    rdb.meta.clear(),
  ]);
}

registerCleanup(clearRecipeData);
